import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShoppingBag, Loader2, Copy, CheckCircle2, Download, X, Eye, EyeOff } from "lucide-react";

function SecretField({ label, value }) {
    const [show, setShow] = useState(false);
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs text-slate-500 mb-2">{label}</p>
            <div className="flex items-center justify-between gap-3">
                <code className="text-lg font-bold text-slate-900 tracking-wider break-all">
                    {show ? value : "\u2022".repeat(Math.min(String(value).length, 16))}
                </code>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => setShow(s => !s)} className="text-slate-500 hover:text-slate-700" title={show ? "Hide" : "Reveal"}>
                        {show ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                    <button onClick={copy} className="text-emerald-600 hover:text-emerald-700" title="Copy">
                        {copied ? <CheckCircle2 size={18} /> : <Copy size={18} />}
                    </button>
                </div>
            </div>
        </div>
    );
}

function OrderModal({ order, onClose }) {
    const code = order.manual_code || order.code;
    const pin = order.manual_pin;
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-bold text-slate-900">Order Details</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
                </div>
                <div className="space-y-3 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Order ID</span><span className="font-mono font-semibold">{order.id}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Product</span><span className="font-semibold">{order.sku}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Price</span><span className="font-semibold">${Number(order.price||0).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Payment</span><span className="capitalize font-semibold">{order.payment_method}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Status</span>
                        <span className={`font-semibold px-2 py-0.5 rounded-full text-xs ${order.status === "completed" ? "bg-emerald-50 text-emerald-700" : order.status === "awaiting_stock" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                            {order.status === "awaiting_stock" ? "Preparing" : order.status}
                        </span>
                    </div>
                    <div className="flex justify-between"><span className="text-slate-500">Date</span><span>{new Date(order.created_at).toLocaleString()}</span></div>
                    {order.manual_expiry && <div className="flex justify-between"><span className="text-slate-500">Expiry</span><span className="font-semibold">{order.manual_expiry}</span></div>}
                </div>

                {order.status === "awaiting_stock" && (
                    <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-4">
                        <p className="text-sm text-blue-700 font-semibold">Preparing your card</p>
                        <p className="text-sm text-slate-600 mt-1">Your payment was received. Your code will be delivered shortly - you will get an email and notification.</p>
                    </div>
                )}

                {code && (
                    <div className="mt-4 space-y-3">
                        <SecretField label="Your gift card code" value={code} />
                        {pin && <SecretField label="PIN" value={pin} />}
                    </div>
                )}

                {order.manual_redemption_url && (
                    <a href={order.manual_redemption_url} target="_blank" rel="noopener noreferrer"
                        className="mt-3 flex items-center justify-center gap-2 text-sm font-semibold text-emerald-600 bg-emerald-50 px-4 py-3 rounded-xl hover:bg-emerald-100">
                        Redeem your card
                    </a>
                )}
                {order.manual_note && (
                    <div className="mt-3 bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700">
                        {order.manual_note}
                    </div>
                )}

                {order.image?.downloadLink && (
                    <a href={order.image.downloadLink} target="_blank" rel="noopener noreferrer"
                        className="mt-3 flex items-center justify-center gap-2 text-sm font-semibold text-emerald-600 bg-emerald-50 px-4 py-3 rounded-xl hover:bg-emerald-100">
                        <Download size={16} /> Download Card Image
                    </a>
                )}
                {order.status === "pending_payment" && order.payment_method === "etransfer" && (
                    <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-4">
                        <p className="text-xs text-blue-600 font-semibold mb-2">E-Transfer Instructions</p>
                        <p className="text-sm text-slate-700">Send <strong>CA${Number(order.price_cad||0).toFixed(2)}</strong> to <strong>support@gifttocash.com</strong></p>
                        <p className="text-sm text-slate-700 mt-1">Reference: <strong>{order.id}</strong></p>
                    </div>
                )}
                <button onClick={onClose} className="mt-4 w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2.5 rounded-xl">Close</button>
            </div>
        </div>
    );
}

const statusBadge = (s) => {
    if (s === "completed") return "bg-emerald-50 text-emerald-700";
    if (s === "awaiting_stock") return "bg-blue-50 text-blue-700";
    if (s === "pending_payment") return "bg-amber-50 text-amber-700";
    return "bg-slate-50 text-slate-600";
};

const statusLabel = (s) => {
    if (s === "pending_payment") return "Pending Payment";
    if (s === "awaiting_stock") return "Preparing";
    return s;
};

export default function MyGiftCardOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        api.get("/prepaidforge/orders")
            .then(r => { setOrders(r.data); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-emerald-500" />
        </div>
    );

    return (
        <div className="space-y-5">
            {selected && <OrderModal order={selected} onClose={() => setSelected(null)} />}

            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold text-slate-900">Gift Card Orders</h1>
                    <p className="text-sm text-slate-500">Your purchase history</p>
                </div>
            </div>

            {orders.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-12 text-center">
                    <div className="flex justify-center mb-4"><ShoppingBag size={40} className="text-slate-300" /></div>
                    <p className="text-slate-500 mb-4">No orders yet</p>
                    <a href="/buy-giftcard" className="bg-emerald-500 text-white font-bold px-6 py-3 rounded-xl inline-block">Browse Gift Cards</a>
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                                <th className="text-left px-5 py-3">Order</th>
                                <th className="text-left px-5 py-3">Product</th>
                                <th className="text-left px-5 py-3">Payment</th>
                                <th className="text-right px-5 py-3">Price</th>
                                <th className="text-left px-5 py-3">Status</th>
                                <th className="text-left px-5 py-3">Date</th>
                                <th className="px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map(o => (
                                <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(o)}>
                                    <td className="px-5 py-3 font-mono text-xs text-slate-500">{o.id}</td>
                                    <td className="px-5 py-3 font-medium text-slate-900 max-w-[150px] truncate">{o.sku}</td>
                                    <td className="px-5 py-3 capitalize text-slate-600">{o.payment_method}</td>
                                    <td className="px-5 py-3 text-right font-semibold">${Number(o.price||0).toFixed(2)}</td>
                                    <td className="px-5 py-3">
                                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${statusBadge(o.status)}`}>
                                            {statusLabel(o.status)}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3 text-xs text-slate-400">{new Date(o.created_at).toLocaleDateString()}</td>
                                    <td className="px-5 py-3 text-right">
                                        {(o.manual_code || o.code) && <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">View Code</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
